// Curated prompt questions (issue #216, extended by #217's structured-
// format goal). Platform-authored, not user-authored -- the user picks
// a question from here rather than inventing their own from a blank
// page (the same blank-page problem #211's auto-generated handle
// already fixed for the handle field).
//
// Grouped into categories (founder direction, 2026-09-16): the full
// bank used to render as one long dump of every question at once --
// two truths and a lie, coffee-or-tea, and everything else, all in one
// undifferentiated list. Categorized + randomized (2 shown per
// category, reshufflable) keeps the picker short and lets a user see a
// fresh set without scrolling the entire bank every time.
//
// Two entry types (#217's own scope: "structured/gamified text
// prompts... still text, but a more engaging INPUT shape than a blank
// textarea", picked as the first #217 format to ship -- lowest
// investment of the options that issue lists, no new infra):
// - 'freeform': the original #216 shape -- pick the question, write
//   your own short answer in a text box.
// - 'this-or-that': pick the question, then pick ONE of two fixed
//   options -- no typing at all. Renders through the exact same
//   domain.Prompt {Question, Answer} shape as a freeform prompt (the
//   picked option's own text becomes the Answer) -- no backend change,
//   same as #216.
//
// Question strings (and, for this-or-that, option strings) are copied
// verbatim into the profile at save time, not stored as a bank
// reference/id -- same documented tradeoff as #216: a referenced id
// would let edits to the bank's wording retroactively reword something
// a user already published, which is the wrong default for a small,
// static, founder-curated list at this stage.
export type PromptCategory = 'Neighborhood' | 'About me' | 'Looking for' | 'This or that';

export const PROMPT_CATEGORIES: PromptCategory[] = ['Neighborhood', 'About me', 'Looking for', 'This or that'];

export type PromptBankEntry =
  | { type: 'freeform'; category: PromptCategory; question: string }
  | { type: 'this-or-that'; category: PromptCategory; question: string; optionA: string; optionB: string };

// Issue #294: the critique's own count -- "six of eight prompts are
// generic dating-app copy in a neighbor-proximity app" -- was real,
// concentrated almost entirely in "Looking for" (three purely dating-
// app prompts) and "This or that" (six purely generic icebreakers with
// zero proximity content). Founder's own explicit direction, same day:
// rewrite the bank, but keep it a genuine MIX of fun/flirty AND
// proximity content -- "don't strip the dating element entirely, just
// stop being purely a Hinge clone." Every category below now blends
// both registers rather than being 100% one or the other; nothing here
// is purely wholesome-only, matching that instruction directly.
export const PROMPT_BANK: PromptBankEntry[] = [
  { type: 'freeform', category: 'Neighborhood', question: 'A local spot everyone around here should know about...' },
  { type: 'freeform', category: 'Neighborhood', question: "You'll probably see me around..." },
  { type: 'freeform', category: 'Neighborhood', question: 'The best-kept secret within walking distance is...' },
  { type: 'freeform', category: 'Neighborhood', question: 'My ideal weekend near here looks like...' },
  { type: 'freeform', category: 'Neighborhood', question: "I'll bribe you with a coffee run to..." },
  { type: 'freeform', category: 'About me', question: 'Two truths and a lie about me...' },
  { type: 'freeform', category: 'About me', question: "I'm overly competitive about..." },
  {
    type: 'freeform',
    category: 'About me',
    question: 'A skill I have that comes up more than you would expect...',
  },
  { type: 'freeform', category: 'About me', question: 'My most unpopular opinion is...' },
  { type: 'freeform', category: 'About me', question: "This year I'm trying to..." },
  { type: 'freeform', category: 'Looking for', question: "I'm looking for someone who..." },
  { type: 'freeform', category: 'Looking for', question: 'The way to win me over is...' },
  { type: 'freeform', category: 'Looking for', question: 'The perfect low-key first hangout near here is...' },
  {
    type: 'this-or-that',
    category: 'This or that',
    question: 'Walk there or drive?',
    optionA: 'Walk',
    optionB: 'Drive',
  },
  {
    type: 'this-or-that',
    category: 'This or that',
    question: 'Local coffee shop or the chain everyone knows?',
    optionA: 'Local spot',
    optionB: 'The chain',
  },
  { type: 'this-or-that', category: 'This or that', question: 'Coffee or tea?', optionA: 'Coffee', optionB: 'Tea' },
  {
    type: 'this-or-that',
    category: 'This or that',
    question: 'Morning person or night owl?',
    optionA: 'Morning person',
    optionB: 'Night owl',
  },
  { type: 'this-or-that', category: 'This or that', question: 'Text or call?', optionA: 'Text', optionB: 'Call' },
  { type: 'this-or-that', category: 'This or that', question: 'Dogs or cats?', optionA: 'Dogs', optionB: 'Cats' },
  {
    type: 'this-or-that',
    category: 'This or that',
    question: 'First date: coffee or a walk?',
    optionA: 'Coffee',
    optionB: 'A walk',
  },
];

// Fisher-Yates, not `.sort(() => Math.random() - 0.5)` -- the sort-
// comparator trick is a well-known bad shuffle (browsers' sort
// implementations aren't guaranteed to call the comparator on every
// pair, so it visibly skews toward certain orderings, especially on
// small arrays like a 3-4-item category). Pure and exported so the
// picker's own randomize step and this file's tests share one real
// implementation instead of duplicating shuffle logic.
export function shuffle<T>(items: T[]): T[] {
  const result = [...items];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

// Two random (non-repeating within the category), not the full
// category list -- founder direction: "pick two randomly from each
// category and have a randomise button next to them". Categories with
// fewer than 2 entries just return everything they have.
export function pickTwoPerCategory(bank: PromptBankEntry[]): PromptBankEntry[] {
  return PROMPT_CATEGORIES.flatMap((category) =>
    shuffle(bank.filter((entry) => entry.category === category)).slice(0, 2),
  );
}
